import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, notification }             from 'antd';
import React                                from 'react';
import { observer }                         from 'mobx-react';
import { Link }                             from 'react-router-dom';
import { MODE_VIEW }                        from 'forms/StandistForm/StandistForm';
import rights                               from 'utils/rights';
import FormattedMessageWrapper              from 'components/App/FormattedMessageWrapper';
import StandDocumentStatus                  from '../../StandDocumentStatus/StandDocumentStatus';
import Status                               from '../Status';
import CardBox                              from 'components/App/CardBox';
import GridInfo                             from 'components/App/GridInfo';
import { AgencyStandists }                  from 'components/App/StandistStandists';
import { DecoratorStandists }               from 'components/App/StandistStandists';
import { ExhibitorStandists }               from 'components/App/StandistStandists';
import { MODE_EDIT }                        from 'forms/StandistForm/StandistForm';
import { ApiCollection }                    from 'collections/ApiCollection';
import { Collection }                       from '@mathquis/modelx';
import UserModel                            from 'models/UserModel';
import StandModel                           from 'models/StandModel';
import UserStandRoleModel                   from 'models/UserStandRoleModel';
import StandDocumentStatusModel             from 'models/StandDocumentStatusModel';
import { formatMessageIntl }                from 'utils/messageIntl';

interface ContentProps {
  agencyVirtualCollection: Collection<UserModel>;
  decoratorVirtualCollection: Collection<UserModel>;
  exhibitorVirtualCollection: Collection<UserModel>;
  externalSync: boolean;
  fetchStandistsWithRoles: () => void;
  stand: StandModel;
  userStandRoleCollection: ApiCollection<UserStandRoleModel>;
}

const Content = observer(
  ({
    agencyVirtualCollection,
    decoratorVirtualCollection,
    exhibitorVirtualCollection,
    externalSync,
    fetchStandistsWithRoles,
    stand,
    userStandRoleCollection,
  }: ContentProps) => {
    const data = [
      [
        {
          label: <FormattedMessageWrapper id="Hall" />,
          value: stand.hall,
        },
        {
          label: <FormattedMessageWrapper id="Allée" />,
          value: stand.aisle,
        },
        {
          label: <FormattedMessageWrapper id="Numéro" />,
          value: stand.number,
        },
      ],
      [
        {
          label: <FormattedMessageWrapper id="Type" />,
          value: stand.standType,
        },
        {
          label: <FormattedMessageWrapper id="Surface" />,
          value: stand.surface,
        },
        {
          label: <FormattedMessageWrapper id="BAT" />,
          value: stand.bat ? 'Oui' : 'Non',
        },
      ],
      [
        {
          label: <FormattedMessageWrapper id="Exposant(s)" />,
          value: (
            <ExhibitorStandists
              externalSync={externalSync}
              mode={rights.for.userStandRole.edit ? MODE_EDIT : MODE_VIEW}
              onSuccess={fetchStandistsWithRoles}
              stand={stand}
              standistCollection={exhibitorVirtualCollection}
              userStandRoleCollection={userStandRoleCollection}
            />
          ),
        },
        {
          label: <FormattedMessageWrapper id="Décorateur(s)" />,
          value: (
            <DecoratorStandists
              externalSync={externalSync}
              mode={rights.for.userStandRole.edit ? MODE_EDIT : MODE_VIEW}
              onSuccess={fetchStandistsWithRoles}
              stand={stand}
              standistCollection={decoratorVirtualCollection}
              userStandRoleCollection={userStandRoleCollection}
            />
          ),
        },
        {
          label: <FormattedMessageWrapper id="Agence(s)" />,
          value: (
            <AgencyStandists
              externalSync={externalSync}
              mode={rights.for.userStandRole.edit ? MODE_EDIT : MODE_VIEW}
              onSuccess={fetchStandistsWithRoles}
              stand={stand}
              standistCollection={agencyVirtualCollection}
              userStandRoleCollection={userStandRoleCollection}
            />
          ),
        },
      ],
    ];

    return <GridInfo
      data={data}
      gridStyle={{
        gridTemplateColumns: '1fr 1fr',
      }}
    />;
  },
);

interface StandDashboardHeaderProps {
  agencyVirtualCollection: Collection<UserModel>;
  decoratorVirtualCollection: Collection<UserModel>;
  dropdownBtn?: any;
  exhibitorVirtualCollection: Collection<UserModel>;
  externalSync: boolean;
  fetchStandistsWithRoles: () => void;
  isLoading?: boolean;
  stand: StandModel;
  standDocumentStatusCollection?: ApiCollection<StandDocumentStatusModel>;
  userStandRoleCollection: ApiCollection<UserStandRoleModel>;
}

@observer
class StandDashboardHeader extends React.Component<StandDashboardHeaderProps> {
  render() {
    const {
      agencyVirtualCollection,
      decoratorVirtualCollection,
      dropdownBtn,
      exhibitorVirtualCollection,
      externalSync,
      fetchStandistsWithRoles,
      isLoading,
      stand,
      standDocumentStatusCollection,
      userStandRoleCollection,
    } = this.props;

    return (
      <CardBox
        content={
          <Content
            agencyVirtualCollection={agencyVirtualCollection}
            decoratorVirtualCollection={decoratorVirtualCollection}
            exhibitorVirtualCollection={exhibitorVirtualCollection}
            externalSync={externalSync}
            fetchStandistsWithRoles={fetchStandistsWithRoles}
            stand={stand}
            userStandRoleCollection={userStandRoleCollection}
          />
        }
        extra={
          <div style={{ alignItems: 'center', display: 'flex' }}>
            {rights.for.stand.markAsTreated && !stand.treated && (
              <div style={{ marginRight: 16 }}>
                <Button
                  type="primary"
                  ghost
                  icon={<CheckOutlined />}
                  onClick={async () => {
                    try {
                      await stand.patch({ treated: true });
                      notification.success({
                        title: formatMessageIntl("Le stand a bien été marqué comme traité"),
                      });
                    } catch (error) {
                      console.error(error);
                      notification.error({
                        title: formatMessageIntl("Une erreur est survenue lors de la mise à jour du stand"),
                      });
                    }
                  }}
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <FormattedMessageWrapper id="Marquer comme traité" />
                </Button>
              </div>
            )}
            {standDocumentStatusCollection ? (
              <div style={{ marginLeft: 24 }}>
                <StandDocumentStatus
                  stand={stand}
                  standDocumentStatusCollection={standDocumentStatusCollection}
                />
              </div>
            ) : (
              <div>
                <Status additionalClass="status-small" status={stand.get('status', 1)} />
              </div>
            )}
            <div style={{ marginLeft: 24 }}>{dropdownBtn}</div>
          </div>
        }
        loading={isLoading}
        title={
          standDocumentStatusCollection ? (
            <div style={{ alignItems: 'center', display: 'flex' }}>
              {stand.name}
              {` `}
              <Link style={{ marginLeft: '8px' }} to={`/stands/${stand.id}`}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  style={{ alignItems: 'center', display: 'flex' }}
                >
                  <FormattedMessageWrapper id="Retour" />
                </Button>
              </Link>
            </div>
          ) : (
            <>{stand.name}</>
          )
        }
      />
    );
  }
}

export default StandDashboardHeader;
